import React from 'react';
import { Container, Row, Col, Card, Button, Table, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';

const Cart = () => {
  const { items, removeFromCart, updateQuantity, getTotal, clearCart } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const getPriceForItem = (item) => {
    if (!item.priceTiers || item.priceTiers.length === 0) {
      return item.basePrice;
    }

    const sortedTiers = [...item.priceTiers].sort((a, b) => b.quantity - a.quantity);
    for (const tier of sortedTiers) {
      if (item.quantity >= tier.quantity) {
        return tier.pricePerUnit;
      }
    }
    return item.basePrice;
  };

  const handleCheckout = () => {
    if (!user) {
      navigate('/login?redirect=/checkout');
    } else {
      navigate('/checkout');
    }
  };

  if (items.length === 0) {
    return (
      <Container className="my-5 text-center">
        <h2>Your cart is empty</h2>
        <p className="text-muted">Add some products to get started!</p>
        <Button as={Link} to="/products" variant="primary">
          Browse Products
        </Button>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <h1 className="mb-4">Shopping Cart</h1>
      
      <Row>
        <Col lg={8}>
          <Card>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Price</th>
                    <th>Quantity</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const pricePerUnit = getPriceForItem(item);
                    return (
                      <tr key={item._id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <img 
                              src={item.image || 'https://via.placeholder.com/100'} 
                              alt={item.name}
                              style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                              className="me-3"
                            />
                            <div>
                              <h6 className="mb-1">{item.name}</h6>
                              <small className="text-muted">{item.category}</small>
                            </div>
                          </div>
                        </td>
                        <td>₹{pricePerUnit}</td>
                        <td>
                          <Form.Control
                            type="number"
                            min="1"
                            max={item.stockCount}
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item._id, parseInt(e.target.value) || 1)}
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <strong>₹{pricePerUnit * item.quantity}</strong>
                        </td>
                        <td>
                          <Button 
                            variant="danger" 
                            size="sm"
                            onClick={() => removeFromCart(item._id)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              
              <div className="text-end">
                <Button variant="outline-danger" onClick={clearCart}>
                  Clear Cart
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card>
            <Card.Header>
              <h5>Order Summary</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal:</span>
                <strong>₹{getTotal()}</strong>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span>Items:</span>
                <span>{items.reduce((sum, item) => sum + item.quantity, 0)}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between mb-3">
                <h5>Total:</h5>
                <h5 className="text-primary">₹{getTotal()}</h5>
              </div>
              
              <div className="d-grid gap-2">
                <Button 
                  variant="success" 
                  size="lg"
                  onClick={handleCheckout}
                >
                  Proceed to Checkout
                </Button>
                <Button 
                  as={Link} 
                  to="/products" 
                  variant="outline-primary"
                >
                  Continue Shopping
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="mt-3">
            <Card.Body>
              <h6>Delivery Information</h6>
              <ul className="small mb-0">
                <li>Instant delivery via Email & WhatsApp</li>
                <li>100% genuine software licenses</li>
                <li>Secure payment with Razorpay</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Cart;
